import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Tooltip as ReactTooltip } from "react-tooltip"
import classNames from "classnames"
import dayjs from "dayjs"
import styled from "styled-components"

import "components/common/Tooltip/tooltip.css"

import { useLazyGetUserProfileQuery } from "store/api/profileApi"
import { useAppSelector } from "store/hooks"
import { OldSubscriptionPlanTitles } from "constants/SubscriptionPlanTitles"
import { OldSubscriptionPlanTitlesWithPrefix } from "constants/SubscriptionPlanTitlesWithPrefix"
import { ApiTypes } from "types"
import { StorageKeys } from "types/enums/StorageKeys"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import EPlusLogo from "assets/img/e-plus-logo.png"
import { ReactComponent as IconCheck } from "assets/svg/icon-check-subscription.svg"
import { ReactComponent as IconEmail } from "assets/svg/icon-email.svg"
import { ReactComponent as IconLogout } from "assets/svg/icon-logout.svg"
import { ReactComponent as IconUpgradePlan } from "assets/svg/icon-upgrade-plan.svg"

import { isRestrictedSubscriptionPlan } from "utils/isRestrictedSubscriptionPlan"
import { setStorageItem } from "utils/localStorage"
import { useEnterpriseMasterAccountSwitch } from "hooks/useEnterpriseMasterAccountSwitch"
import { useLogout } from "hooks/useLogout"
import { ModalLayout } from "layouts/ModalLayout"
import { UniversityIcon } from "components/topbars/ContractorTopbar/UniversityIcon"

import subscriptionPlanFeatures from "./SubscriptionPlanFeatures"

import styles from "./index.module.scss"

const EPlusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  height: 50px !important;

  img {
    width: 42px;
    height: 42px;
    margin-left: 15px;
  }

  span {
    font-size: 30px !important;
    font-style: normal !important;
    color: #000;
  }
`

const CurrentPlanLabel = styled.div`
  white-space: nowrap;
`

const IconWrapperBase = styled.div<{ $right?: string }>`
  position: absolute;
  top: 2px;
  right: ${props => props.$right || "10px"};
  z-index: 6002;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  transition: background 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 1);

    svg,
    svg path {
      fill: #2db2c8;
      transition: fill 0.2s;
    }
  }
`

const UniversityIconWrapper = styled(IconWrapperBase)`
  right: 60px;

  > div {
    margin-left: 0 !important;
  }
`

const LogoutIconWrapper = styled(IconWrapperBase)`
  right: 10px;

  svg,
  svg path {
    fill: white;
    transition: fill 0.2s;
  }
`

const upgradeMessages = {
  [SubscriptionPrivilege.PerformanceTest]: "Performance Test is unavailable",
  [SubscriptionPrivilege.Alerts]: "Alerts is unavailable",
  [SubscriptionPrivilege.Monitoring]: "Monitoring is unavailable",
  [SubscriptionPrivilege.MessageCenter]: "Messages are unavailable",
  [SubscriptionPrivilege.IntelligenceReports]: "Intelligence Reports is unavailable",
}

// const planPrices = {
//   [SubscriptionPlan.NETWORK]: ["1,498"],
//   [SubscriptionPlan.PLUS]: ["1,089"],
//   [SubscriptionPlan.BASIC]: [789],
//   [SubscriptionPlan.GO]: ["0"],
//   [SubscriptionPlan.CONNECT]: ["0"],
//   [SubscriptionPlan.BASIC_MAY_2025]: ["698"],
//   [SubscriptionPlan.E_PLUS]: ["1,987"],
// }

interface SubscriptionUpgradeModalProps {
  onCancel?: () => void
  page?: SubscriptionPrivilege
  hideUnavailableModal?: boolean
}

export const SubscriptionUpgradeModal = ({ onCancel, hideUnavailableModal, page }: SubscriptionUpgradeModalProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ id?: string }>()
  const logout = useLogout()
  const [triggerGetProfile] = useLazyGetUserProfileQuery()
  const [showUnavailableModal, setShowUnavailableModal] = useState(!hideUnavailableModal)
  const { hasEnterpriseMasterAccountToken, switchBackToEnterpriseMasterAccount } = useEnterpriseMasterAccountSwitch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const current_subscription = userProfile?.contractor?.subscription_plan || SubscriptionPlan.BASIC_MAY_2025
  const isRestricted = isRestrictedSubscriptionPlan(userProfile)

  useEffect(() => {
    const getProfile = async () => {
      try {
        const res = await triggerGetProfile()

        if (!res?.error) {
          const lastSync = dayjs()
          const data = { ...res?.data?.data, lastSync }

          if (
            (userProfile?.contractor?.subscription_plan || SubscriptionPlan.BASIC) !==
            data?.contractor?.subscription_plan
          ) {
            setStorageItem(StorageKeys.UserProfile, data as ApiTypes.Model.UserProfile)
            window.location.reload()
          }
        }
      } catch (error) {
        console.error(error)
      }
    }

    getProfile()
  }, [])

  const getBasicPlan = () => {
    if (current_subscription === SubscriptionPlan.BASIC) {
      return SubscriptionPlan.BASIC
    } else if (current_subscription === SubscriptionPlan.BASIC_OCT_2025) {
      return SubscriptionPlan.BASIC_OCT_2025
    } else if (current_subscription === SubscriptionPlan.BASIC_DEC_2025) {
      return SubscriptionPlan.BASIC_DEC_2025
    } else if (current_subscription === SubscriptionPlan.BASIC_MAY_2026) {
      return SubscriptionPlan.BASIC_MAY_2026
    } else if (current_subscription === SubscriptionPlan.SUPREME) {
      return SubscriptionPlan.SUPREME
    }

    return SubscriptionPlan.BASIC_JULY_2026
  }

  const getNetworkPlan = () => {
    if (current_subscription === SubscriptionPlan.NETWORK) {
      return SubscriptionPlan.NETWORK
    }

    return SubscriptionPlan.NETWORK_JULY_2026
  }

  const plans = useMemo(() => {
    return [
      SubscriptionPlan.E_PLUS,
      getNetworkPlan(),
      ...(current_subscription === SubscriptionPlan.PLUS ? [SubscriptionPlan.PLUS] : []),
      getBasicPlan(),
      ...(current_subscription === SubscriptionPlan.GROWTH || current_subscription === SubscriptionPlan.GROW_MARCH_2026
        ? [current_subscription]
        : [SubscriptionPlan.START]),
    ]
  }, [current_subscription])

  const handleCancel = () => {
    if (onCancel) {
      return onCancel()
    }

    if (params.id && location.pathname.startsWith(`/customers/${params.id}`)) {
      navigate(`/customers/${params.id}/overview`)
      return
    }

    navigate("/")
  }

  const handleHideUnavailableModal = () => {
    setShowUnavailableModal(false)
  }

  const renderTitle = (plan: SubscriptionPlan) => {
    if (plan === SubscriptionPlan.E_PLUS) {
      return (
        <EPlusContainer>
          <img src={EPlusLogo} alt="EPlus Logo" />
          <span>+</span>
        </EPlusContainer>
      )
    }

    return (
      <>
        <span>Nuve</span>
        <p className={styles.boldTitle}>{OldSubscriptionPlanTitles[plan]}</p>
      </>
    )
  }

  if (showUnavailableModal && page) {
    return (
      <div className={styles.subscriptionSubModal}>
        <ModalLayout
          width="454px"
          buttonSize="fullWidth"
          confirmButtonText="Upgrade Plan"
          footerClassName={styles.subscriptionSubModalFooter}
          onClose={handleCancel}
          onConfirm={handleHideUnavailableModal}
        >
          <div className={styles.modalBody}>
            <div className={styles.iconWrapper}>
              <IconUpgradePlan />
            </div>
            <h3>{upgradeMessages[page]}</h3>
            <p>
              {upgradeMessages[page]} in <span>“{OldSubscriptionPlanTitlesWithPrefix[current_subscription]}”</span>{" "}
              plan. Please, upgrade your plan to be able use this feature.
            </p>
          </div>
        </ModalLayout>
      </div>
    )
  }

  const renderPlanFeature = (plan: SubscriptionPlan, feature: { name: string; [key: string]: string }) => {
    if (feature[plan] === "check") {
      return <IconCheck />
    }

    // if (feature[plan] === "price") {
    //   const [oldPrice] = planPrices[plan]
    //   return (
    //     <div className={styles.subscriptionModalPrice}>
    //       <div className={styles.subscriptionModalPriceOld}>{oldPrice === "0" ? "-" : <span>${oldPrice}</span>}</div>
    //     </div>
    //   )
    // }

    return feature[plan] || ""
  }

  const renderFeatures = () => (
    <div className={classNames(styles.subscriptionModalContent, styles.subscriptionModalFeature)}>
      <div className={styles.subscriptionModalRowTitle}>
        <p className={styles.featuresTitle}>Features</p>
      </div>
      {subscriptionPlanFeatures.map(feature => (
        <div className={classNames(styles.subscriptionModalRow)} key={feature.name}>
          <p>{feature.name}</p>
        </div>
      ))}
    </div>
  )

  const handleBack = async () => {
    if (hasEnterpriseMasterAccountToken) {
      await switchBackToEnterpriseMasterAccount()
    } else {
      await logout()
    }
  }

  return (
    <div className={styles.subscriptionModal}>
      {isRestricted && (
        <>
          <UniversityIconWrapper>
            <UniversityIcon />
          </UniversityIconWrapper>
          <LogoutIconWrapper onClick={handleBack} data-tooltip-id="tooltip-logout">
            <IconLogout />
          </LogoutIconWrapper>
        </>
      )}
      <ReactTooltip delayShow={500} className={styles.tooltip} place="bottom-start" id="tooltip-logout">
        Logout
      </ReactTooltip>
      <ModalLayout width="1000px" onClose={isRestricted ? undefined : handleCancel}>
        <div className={styles.subscriptionModalContainer}>
          <div className={styles.subscriptionModalWrapper}>
            <div className={styles.sectionFeaturesWrapper}>{renderFeatures()}</div>
            {plans.map(plan => (
              <div key={plan} className={styles.subscriptionModalSection}>
                {renderFeatures()}
                <div className={styles.subscriptionModalContent}>
                  {(plan === SubscriptionPlan.NETWORK || plan === SubscriptionPlan.NETWORK_JULY_2026) && (
                    <div className={styles.popularPlan}>
                      <div className={styles.mostPopularText}>
                        <p>most Popular</p>
                      </div>
                    </div>
                  )}
                  <div className={classNames(styles.subscriptionModalRowTitle, styles.popularPlanTitle)}>
                    {renderTitle(plan)}
                    <CurrentPlanLabel>{current_subscription === plan ? "Current Plan" : ""}</CurrentPlanLabel>
                  </div>
                  {subscriptionPlanFeatures.map(feature => (
                    <div className={styles.subscriptionModalRow} key={feature.name}>
                      {renderPlanFeature(plan, feature)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className={styles.subscriptionModalFooter}>
            <div className={styles.subscriptionModalFooterText}>
              <span>Contact your CSR to upgrade your Plan</span>
            </div>
            <div className={styles.subscriptionModalFooterContacts}>
              <span>
                <IconEmail /> support@nuvehome.com
              </span>
            </div>
          </div>
        </div>
      </ModalLayout>
    </div>
  )
}
